import React, { useRef } from 'react';
import '../../Configuration/UpdateCard/Transaction/TxnPricingTable.css';

export interface IReferralTier {
  min: number;
  max: number | '';
  bips: number;
}

export interface IBipsValue {
  tiered: boolean;
  flatBips: number;
  tiers: IReferralTier[];
}

interface IBipsEditorProps {
  value: IBipsValue;
  onChange: (v: IBipsValue) => void;
  invoicingCurrency: string;
}

interface ITierError {
  min?: string;
  max?: string;
}

// Only range and contiguity checks — does NOT include "last tier must be unlimited"
// so that error never blocks the Add Tier button.
function computeTierErrors(tiers: IReferralTier[]): ITierError[] {
  return tiers.map((t, i) => {
    const err: ITierError = {};
    const minVal = t.min ?? 0;
    const maxVal = t.max;

    if (maxVal !== '' && Number(maxVal) <= minVal) {
      err.max = `Must be > ${minVal}`;
    }
    if (i > 0) {
      const prevMax = tiers[i - 1].max;
      if (prevMax === '') {
        err.min = 'Previous tier has no upper limit';
      } else if (minVal !== Number(prevMax)) {
        err.min = `Must be ${prevMax}`;
      }
    }
    return err;
  });
}

export function hasBipsValidationErrors(value: IBipsValue): boolean {
  if (!value.tiered) return false;
  const tiers: IReferralTier[] = value.tiers?.length
    ? value.tiers
    : [{ min: 0, max: '', bips: 0 }];
  const hasRangeError = computeTierErrors(tiers).some((e) => e.min || e.max);
  const lastTierBounded = tiers[tiers.length - 1].max !== '';
  return hasRangeError || lastTierBounded;
}

const BipsEditor: React.FC<IBipsEditorProps> = ({ value, onChange, invoicingCurrency }) => {
  const uid = useRef(`bips-${Math.random().toString(36).slice(2)}`).current;
  const tiered = value.tiered ?? false;
  const flatBips = value.flatBips ?? 0;
  const tiers: IReferralTier[] = value.tiers?.length
    ? value.tiers
    : [{ min: 0, max: '', bips: 0 }];

  const s = (patch: Partial<IBipsValue>) => onChange({ ...value, ...patch });

  const tierErrors = computeTierErrors(tiers);
  const hasAnyError = tierErrors.some((e) => e.min || e.max);
  // Infinite = last tier has no upper bound (blank). Add Tier is only useful when
  // the last tier IS bounded — once it's infinite there's nowhere to add after it.
  const lastTierIsInfinite = tiers.length > 0 && tiers[tiers.length - 1].max === '';
  const addDisabled = lastTierIsInfinite || hasAnyError;
  let addTierTitle: string | undefined;
  if (lastTierIsInfinite) {
    addTierTitle = 'Last tier is already unlimited — set a Max Vol first to add another tier';
  } else if (hasAnyError) {
    addTierTitle = 'Fix tier errors before adding another tier';
  }

  const updateTierMax = (i: number, newMax: number | '') => {
    const next = tiers.map((t) => ({ ...t }));
    next[i] = { ...next[i], max: newMax };
    if (i + 1 < next.length) {
      next[i + 1] = { ...next[i + 1], min: newMax === '' ? 0 : Number(newMax) };
    }
    s({ tiers: next });
  };

  const updateTierBips = (i: number, bips: number) => {
    const next = tiers.map((t) => ({ ...t }));
    next[i] = { ...next[i], bips };
    s({ tiers: next });
  };

  const addTier = () => {
    const last = tiers[tiers.length - 1];
    if (last.max === '') return;
    s({
      tiers: [...tiers, { min: Number(last.max), max: '', bips: 0 }],
    });
  };

  const removeTier = (i: number) => {
    const filtered = tiers.filter((_, j) => j !== i);
    if (i < filtered.length) {
      let newMin = 0;
      if (i > 0) {
        newMin = filtered[i - 1].max === '' ? 0 : Number(filtered[i - 1].max);
      }
      filtered[i] = { ...filtered[i], min: newMin };
    }
    s({ tiers: filtered.length ? filtered : [{ min: 0, max: '', bips: 0 }] });
  };

  return (
    <div className="bips-editor">
      <div className="tcp-mode-bar">
        <label
          className={`tcp-mode-radio-lbl${!tiered ? ' tcp-mode-radio-lbl--on' : ''}`}
          htmlFor={`${uid}-flat`}
        >
          <input
            checked={!tiered}
            className="tcp-mode-radio-input"
            id={`${uid}-flat`}
            name={`${uid}-mode`}
            onChange={() => s({ tiered: false })}
            type="radio"
            value="flat"
          />
          Flat Rate
        </label>
        <label
          className={`tcp-mode-radio-lbl${tiered ? ' tcp-mode-radio-lbl--on' : ''}`}
          htmlFor={`${uid}-tiered`}
        >
          <input
            checked={tiered}
            className="tcp-mode-radio-input"
            id={`${uid}-tiered`}
            name={`${uid}-mode`}
            onChange={() => s({ tiered: true })}
            type="radio"
            value="tiered"
          />
          Tiered
        </label>
        <span className="tcp-mode-desc">
          {tiered ? 'Volume-based tier bands below' : 'Single rate for all transaction amounts'}
        </span>
      </div>

      {!tiered ? (
        <div className="bips-flat-field">
          <div className="bips-label">Basis Points (bps)</div>
          <input
            aria-label="Flat basis points"
            className="bips-input"
            max={10000}
            min={0}
            onChange={(e) => s({ flatBips: Number(e.target.value) })}
            step="0.01"
            type="number"
            value={flatBips}
          />
        </div>
      ) : (
        <div>
          <div className="bips-tier-header">
            <span className="bips-label">Tier Ranges</span>
          </div>

          <div className="bips-tier-cols">
            <div>
              From
            </div>
            <div>
              To
            </div>
            <div>BiPs ({invoicingCurrency})</div>
            <div />
          </div>

          {tiers.map((t, idx) => {
            const isLast = idx === tiers.length - 1;
            const errs = tierErrors[idx];
            // Last tier must remain unlimited — shown as inline warning but does NOT
            // block Add Tier so the user can click it to resolve the situation.
            const lastTierBounded = isLast && t.max !== '';
            const toHasError = !!errs.max || lastTierBounded;
            return (
              <div key={`tier-min-${t.min}`}>
                <div className="bips-tier-row">
                  <input
                    aria-label={`Tier ${idx + 1} from`}
                    className={`bips-input-sm${errs.min ? ' tcp-inp-err' : ''}`}
                    disabled={idx === 0}
                    step="0.01"
                    title="Lower bound — auto-set from previous tier upper limit"
                    type="number"
                    value={t.min}
                  />
                  <input
                    aria-label={`Tier ${idx + 1} to`}
                    className={`bips-input-sm${toHasError ? ' tcp-inp-err' : ''}`}
                    onChange={(e) => updateTierMax(
                      idx,
                      e.target.value === '' ? '' : Number(e.target.value),
                    )}
                    placeholder={isLast ? '∞ (no limit)' : ''}
                    step="0.01"
                    type="number"
                    value={t.max}
                  />
                  <input
                    aria-label={`Tier ${idx + 1} bips`}
                    className="bips-input-sm"
                    max={10000}
                    min={0}
                    onChange={(e) => updateTierBips(idx, Number(e.target.value))}
                    step="0.01"
                    type="number"
                    value={t.bips}
                  />
                  <button
                    aria-label={`Remove tier ${idx + 1}`}
                    className="bips-remove-btn"
                    disabled={tiers.length <= 1}
                    onClick={() => removeTier(idx)}
                    type="button"
                  >
                    ×
                  </button>
                </div>
                {(errs.min || errs.max || lastTierBounded) && (
                  <div className="tcp-row-errors">
                    {errs.min && (
                      <span className="tcp-row-error">
                        △ From:
                        {' '}
                        {errs.min}
                      </span>
                    )}
                    {errs.max && (
                      <span className="tcp-row-error">
                        △ To:
                        {' '}
                        {errs.max}
                      </span>
                    )}
                    {lastTierBounded && !errs.max && (
                      <span className="tcp-row-error">
                        △ To: Last tier must be unlimited — add New Tier and leave blank to cover all transaction amounts
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          <div className="bips-add-tier-row">
            <button
              className="btn btn-outline bips-add-btn"
              disabled={addDisabled}
              onClick={addTier}
              title={addTierTitle}
              type="button"
            >
              + Add Tier
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BipsEditor;
